import { Box, Flex, Group, SegmentedControl, SimpleGrid, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import {
  CategoryTreeMultiSelect,
  CategoryTreeSelect,
  useCategoryImage,
} from "../CategoryTreeSelect";
import { SectionCard } from "../HomeBlockShared";
import { CatGridConfig, CatGridTile } from "../homeBlockConfig";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import classes from "../homeBlock.module.css";
import {
  applyAction,
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconLabelList,
  iconLabelItem,
  IconPicker,
  ImagePicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.catgrid;
const PROMO_CROP: Crop = { x: 16, y: 88, w: 331, h: 160 };

type SampleTile = {
  cat: string;
  icon: string;
  count: string;
  image: string;
  links: string[];
  crop: Crop;
};

const sampleTile = (
  cat: string,
  icon: string,
  count: string,
  image: string,
  links: string[],
  x: number,
  y: number,
  w = 142
): SampleTile => ({ cat, icon, count, image, links, crop: { x, y, w, h: 80 } });

// Dữ liệu mẫu theo thiết kế Figma.
const SAMPLE_TILES: SampleTile[] = [
  sampleTile("Laptop", "laptop", "3.200+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-01-laptop.png", ["Văn phòng", "Gaming", "AI Laptop"], 355, 92),
  sampleTile("Gaming", "gamepad-2", "2.800+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-02-gaming.png", ["Laptop Gaming", "PC Gaming", "Gear"], 505, 92),
  sampleTile("PC", "pc-case", "1.500+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-03-pc.png", ["PC đồ họa", "Mini PC", "Workstation"], 655, 92),
  sampleTile("Tablet", "tablet", "800+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-04-tablet.png", ["iPad", "Android Tablet", "Phụ kiện"], 805, 92),
  sampleTile("Văn Phòng", "printer", "1.200+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-05-office.png", ["Máy in", "Thiết bị văn phòng", "Phụ kiện"], 955, 92),
  sampleTile("Màn hình - TV", "monitor", "900+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-06-monitor.png", ["Màn hình PC", "TV", "Projector"], 1115, 92),
  sampleTile("Camera", "cctv", "600+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-07-camera.png", ["Camera IP", "Camera WiFi", "Camera AI"], 16, 262, 154),
  sampleTile("Linh kiện PC", "cpu", "4.000+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-08-pcparts.png", ["CPU", "VGA", "RAM", "SSD"], 178, 262, 162),
  sampleTile("Thiết bị ngoại vi", "keyboard", "2.500+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-09-peripherals.png", ["Bàn phím", "Chuột", "Webcam"], 350, 262),
  sampleTile("Âm thanh", "speaker", "1.000+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-10-audio.png", ["Tai nghe", "Loa", "Micro"], 502, 262),
  sampleTile("Lưu trữ - Mạng", "wifi", "700+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-11-network.png", ["HDD", "NAS", "Router"], 652, 262),
  sampleTile("Phần mềm", "app-window", "100+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-12-software.png", ["Windows", "Office", "Bảo mật"], 802, 262),
  sampleTile("Ghế Gaming", "armchair", "500+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-13-chair.png", ["Gaming Chair", "Công thái học", "Phụ kiện"], 955, 262),
  sampleTile("Tản nhiệt", "fan", "400+", "https://cdn-files.hacom.vn/hacom/cdn/web/03112026/cat-14-cooling.png", ["Tản nhiệt khí", "Tản nhiệt nước", "Fan case"], 1115, 262),
];

const sampleTileId = (index: number) => `cg-sample-${index + 1}`;
const SAMPLE_CROPS = new Map(SAMPLE_TILES.map((s, i) => [sampleTileId(i), s.crop]));

const emptyTile = (): CatGridTile => ({
  id: newId(),
  category: { id: null, name: "" },
  image: null,
  icon: null,
  displayName: "",
  countMode: "auto",
  countText: "",
  arrowLink: "",
  links: [],
});

const initialConfig = (block: HomeBlock): CatGridConfig => {
  const sample = block.code === "home-featured-categories";
  return {
    heading: {
      eyebrow: sample ? "DANH MỤC NỔI BẬT" : "",
      title: sample ? "DANH MỤC" : block.name,
      highlight: sample ? "NỔI BẬT" : "",
      description: sample
        ? "Khám phá hàng nghìn sản phẩm chính hãng từ các thương hiệu hàng đầu thế giới tại HACOM"
        : "",
      allLabel: sample ? "Xem tất cả danh mục" : "",
      allLink: sample ? "/danh-muc" : "",
    },
    promo: {
      image: sample ? "https://cdn-files.hacom.vn/hacom/cdn/web/2026-10-27/featured-pc-build-hd.png" : null,
      title: sample ? "TỰ XÂY DỰNG\nCẤU HÌNH PC" : "",
      bullets: sample
        ? [
            iconLabelItem("cpu", "Tương thích linh kiện"),
            iconLabelItem("sparkles", "Tối ưu hiệu năng"),
            iconLabelItem("badge-percent", "Tiết kiệm chi phí"),
          ]
        : [],
      buttonLabel: sample ? "BẮT ĐẦU XÂY DỰNG" : "",
      link: sample ? "/buildpcv2" : "",
    },
    tiles: sample
      ? SAMPLE_TILES.map((s, i) => ({
          id: sampleTileId(i),
          category: { id: null, name: s.cat },
          image: s.image,
          icon: lucideIcon(s.icon),
          displayName: s.cat,
          countMode: "manual",
          countText: `${s.count} sản phẩm`,
          arrowLink: "",
          links: s.links.map((name) => ({ id: null, name })),
        }))
      : [],
  };
};

// Ô ảnh của dòng: mặc định lấy ảnh của danh mục đã chọn, bấm để tải ảnh khác hoặc quay về ảnh danh mục
const TileImageCell = ({
  tile,
  update,
}: {
  tile: CatGridTile;
  update: (patch: Partial<CatGridTile>) => void;
}) => {
  const categoryImage = useCategoryImage(tile.category.id);
  return (
    <ThumbPicker
      label={tile.category.name || "ô danh mục"}
      src={tile.image}
      fallbackSrc={categoryImage}
      fallbackNote="Ảnh của danh mục"
      imageId={IMG}
      crop={SAMPLE_CROPS.get(tile.id)}
      onPick={(url) => update({ image: url })}
      onReset={() => update({ image: null })}
      resetLabel="Dùng lại ảnh của danh mục"
    />
  );
};

// Phần mở rộng của một ô: cấu hình hàng tiêu đề (icon, tên, số sản phẩm, nút mũi tên)
const TileHeaderSettings = ({
  t,
  update,
}: {
  t: CatGridTile;
  update: (patch: Partial<CatGridTile>) => void;
}) => (
  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12} pt={4}>
    <IconPicker label="Icon" value={t.icon} onChange={(value) => update({ icon: value })} />
    <TextInput
      label="Tên hiển thị"
      placeholder={t.category.name || "Tên danh mục"}
      description="Để trống thì dùng tên danh mục đã chọn."
      inputWrapperOrder={["label", "input", "description"]}
      value={t.displayName}
      onChange={(e) => update({ displayName: e.currentTarget.value })}
    />
    <Box>
      <Text fz={14} fw={500} mb={6}>
        Số sản phẩm
      </Text>
      <Group gap={8} wrap="nowrap" align="center">
        <SegmentedControl
          value={t.countMode}
          onChange={(value) => update({ countMode: value as CatGridTile["countMode"] })}
          data={[
            { value: "auto", label: "Tự đếm" },
            { value: "manual", label: "Nhập tay" },
            { value: "hidden", label: "Ẩn" },
          ]}
          style={{ flex: "none" }}
        />
        {t.countMode === "manual" && (
          <TextInput
            aria-label="Dòng số sản phẩm"
            placeholder="Ví dụ: 3.200+ sản phẩm"
            value={t.countText}
            onChange={(e) => update({ countText: e.currentTarget.value })}
            style={{ flex: "1 1 auto", minWidth: 0 }}
          />
        )}
      </Group>
      {t.countMode === "auto" && (
        <Text fz={12} mt={6} className={classes.muted}>
          Đếm theo danh mục, làm tròn xuống, ví dụ 3.247 hiện "3.200+ sản phẩm".
        </Text>
      )}
    </Box>
    <TextInput
      label="Link nút mũi tên"
      placeholder="Mặc định: trang của danh mục đã chọn"
      value={t.arrowLink}
      onChange={(e) => update({ arrowLink: e.currentTarget.value })}
    />
  </SimpleGrid>
);

const EditCatGrid = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  // Ảnh quảng bá mẫu cắt từ thiết kế: chỉ để xem, không nằm trong config
  const [showSamplePromo, setShowSamplePromo] = useState(
    block.code === "home-featured-categories"
  );

  const { heading, promo, tiles } = config;
  const patchHeading = (v: Partial<CatGridConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const patchPromo = (v: Partial<CatGridConfig["promo"]>) =>
    setConfig((c) => ({ ...c, promo: { ...c.promo, ...v } }));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn nhỏ"
            value={heading.eyebrow}
            onChange={(e) => patchHeading({ eyebrow: e.currentTarget.value })}
          />
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            description="Hiện màu đỏ, nối sau tiêu đề."
            inputWrapperOrder={["label", "input", "description"]}
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Mô tả"
            value={heading.description}
            onChange={(e) => patchHeading({ description: e.currentTarget.value })}
          />
          <TextInput
            label="Nhãn nút Xem tất cả"
            value={heading.allLabel}
            onChange={(e) => patchHeading({ allLabel: e.currentTarget.value })}
          />
          <TextInput
            label="Link"
            value={heading.allLink}
            onChange={(e) => patchHeading({ allLink: e.currentTarget.value })}
          />
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Ô quảng bá">
        <Flex wrap="wrap" gap={16} align="flex-start">
          <Box style={{ flex: "0 1 360px", minWidth: 220 }}>
            <ImagePicker
              label="Ảnh"
              src={promo.image}
              imageId={showSamplePromo ? IMG : undefined}
              crop={PROMO_CROP}
              onPick={(url) => patchPromo({ image: url })}
              onClear={() => {
                setShowSamplePromo(false);
                patchPromo({ image: null });
              }}
              maxWidth={360}
            />
          </Box>
          <SimpleGrid
            cols={{ base: 1, md: 2 }}
            spacing={12}
            style={{ flex: "1 1 360px", minWidth: 0 }}
          >
            <TextInput
              label="Tiêu đề"
              value={promo.title}
              onChange={(e) => patchPromo({ title: e.currentTarget.value })}
            />
            <TextInput
              label="Nhãn nút"
              value={promo.buttonLabel}
              onChange={(e) => patchPromo({ buttonLabel: e.currentTarget.value })}
            />
            <TextInput
              label="Link"
              value={promo.link}
              onChange={(e) => patchPromo({ link: e.currentTarget.value })}
            />
          </SimpleGrid>
        </Flex>
        <IconLabelList
          label="Gạch đầu dòng"
          items={promo.bullets}
          onChange={(action) =>
            setConfig((c) => ({
              ...c,
              promo: { ...c.promo, bullets: applyAction(action, c.promo.bullets) },
            }))
          }
          max={3}
          addLabel="Thêm dòng"
        />
      </SectionCard>

      <SectionCard
        title="Ô danh mục"
        right={
          <Text fz={13} className={classes.muted}>
            Ảnh lấy từ danh mục, bấm ảnh để đổi. Nút cuối dòng để sửa icon, tên, số sản phẩm, link
          </Text>
        }
      >
        <EditableList
          items={tiles}
          onChange={fieldSetter(setConfig, "tiles")}
          itemLabel={(t) => t.category.name}
          columns={[
            { label: "Ảnh", width: "56px" },
            { label: "Danh mục", width: "minmax(0, 1fr)" },
            { label: "Liên kết con (tối đa 4 danh mục)", width: "minmax(0, 1.6fr)" },
          ]}
          renderCells={(t, update) => (
            <>
              <TileImageCell tile={t} update={update} />
              <CategoryTreeSelect
                ariaLabel="Danh mục"
                value={t.category.id}
                fallbackName={t.category.name}
                onChange={(id, node) =>
                  update({ category: node ? { id: node.id, name: node.name } : { id, name: "" } })
                }
              />
              <CategoryTreeMultiSelect
                ariaLabel={`Liên kết con của ${t.category.name || "ô"}`}
                value={t.links.flatMap((l) => (l.id === null ? [] : [l.id]))}
                fallbackNames={t.links.filter((l) => l.id === null).map((l) => l.name)}
                maxValues={4}
                onChange={(_, nodes) =>
                  update({ links: nodes.map((n) => ({ id: n.id, name: n.name })) })
                }
              />
            </>
          )}
          newItem={emptyTile}
          addLabel="Thêm ô danh mục"
          max={14}
          minWidth={660}
          renderDetails={(t, update) => <TileHeaderSettings t={t} update={update} />}
          detailsLabel="Tuỳ chỉnh hàng tiêu đề"
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditCatGrid;
